import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCreateOrganization, useUpdateOrganization } from '@/hooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import type { Organization } from '@/types'

const orgFormSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  contact_email: z.string().min(1, 'Email is required').email('Invalid email address'),
  plan_tier: z.string().min(1, 'Plan is required'),
  logo_url: z.string().url('Invalid URL').optional().or(z.literal('')),
  primary_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color')
    .optional()
    .or(z.literal('')),
  secondary_color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/, 'Invalid hex color')
    .optional()
    .or(z.literal('')),
})

type OrgFormValues = z.infer<typeof orgFormSchema>

interface OrgFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  organization?: Organization
}

const planOptions = [
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' },
]

export function OrgFormDialog({
  open,
  onOpenChange,
  organization,
}: OrgFormDialogProps) {
  const isEditing = !!organization

  const createMutation = useCreateOrganization()
  const updateMutation = useUpdateOrganization(organization?.id ?? '')

  const form = useForm<OrgFormValues>({
    resolver: zodResolver(orgFormSchema),
    defaultValues: {
      name: organization?.name ?? '',
      contact_email: organization?.contact_email ?? '',
      plan_tier: organization?.plan_tier ?? 'free',
      logo_url: organization?.logo_url ?? '',
      primary_color: organization?.primary_color ?? '',
      secondary_color: organization?.secondary_color ?? '',
    },
  })

  // Reset form when organization changes or dialog opens/closes
  useEffect(() => {
    if (open) {
      form.reset({
        name: organization?.name ?? '',
        contact_email: organization?.contact_email ?? '',
        plan_tier: organization?.plan_tier ?? 'free',
        logo_url: organization?.logo_url ?? '',
        primary_color: organization?.primary_color ?? '',
        secondary_color: organization?.secondary_color ?? '',
      })
    }
  }, [open, organization, form])

  const isPending = createMutation.isPending || updateMutation.isPending

  function onSubmit(values: OrgFormValues) {
    const data = {
      name: values.name,
      contact_email: values.contact_email,
      plan_tier: values.plan_tier,
      logo_url: values.logo_url || undefined,
      primary_color: values.primary_color || undefined,
      secondary_color: values.secondary_color || undefined,
    }

    if (isEditing) {
      updateMutation.mutate(data, {
        onSuccess: () => {
          onOpenChange(false)
        },
      })
    } else {
      createMutation.mutate(data, {
        onSuccess: () => {
          onOpenChange(false)
        },
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Organization' : 'Add Organization'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the organization details below.'
              : 'Enter the details for the new organization.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Organization name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="contact_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contact Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="contact@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="plan_tier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Plan</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a plan" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {planOptions.map((plan) => (
                        <SelectItem key={plan.value} value={plan.value}>
                          {plan.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 grid-cols-2">
              <FormField
                control={form.control}
                name="primary_color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Primary Color</FormLabel>
                    <FormControl>
                      <Input placeholder="#3B82F6" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="secondary_color"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Secondary Color</FormLabel>
                    <FormControl>
                      <Input placeholder="#10B981" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="logo_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Logo URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://example.com/logo.png" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? isEditing
                    ? 'Saving...'
                    : 'Creating...'
                  : isEditing
                    ? 'Save Changes'
                    : 'Add Organization'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
